<script setup lang="ts">
import { __ } from '@@/bits/intl'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { useSurfaceTrashedViewStore } from '@@/pinia/surface_trashed_view'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const { isDarkMode } = storeToRefs(useDarkModeStore())
const { isCurrentUserWallBuilder, redirectUrl } = storeToRefs(useSurfaceTrashedViewStore())

const ORIGINAL_IMAGE_WIDTH = 1011
const ORIGINAL_IMAGE_HEIGHT = 623
const IMAGE_WIDTH = 275

const imageUrl = computed(() => {
  const BASE_IMAGE_URL = `https://padlet.net/trashed_padlet/v0/padlet_trashed_`
  const IMAGE_FILE_EXTENSION = '.webp'
  const imageColor = isDarkMode.value ? 'dark' : 'light'
  return BASE_IMAGE_URL + imageColor + IMAGE_FILE_EXTENSION
})

const description = computed(() => {
  return isCurrentUserWallBuilder.value
    ? __('You can restore it from the Trash section.')
    : __('It can only be restored by the user who moved it to Trash.')
})

const buttonText = computed(() => {
  return isCurrentUserWallBuilder.value ? __('Go to trash') : __('Go to dashboard')
})
</script>
<template>
  <div class="space-y-6 max-w-[288px]">
    <ImageThumbnail
      class="mx-1"
      :src="imageUrl"
      title="padletTrashedImage"
      :fallback-to-placeholder="true"
      :original-image-width="ORIGINAL_IMAGE_WIDTH"
      :original-image-height="ORIGINAL_IMAGE_HEIGHT"
      :width="IMAGE_WIDTH"
      resize-strategy="crop"
      aria-hidden="true"
      :lazy-loading="true"
      :process-image="true"
      :aspect-ratio="ORIGINAL_IMAGE_WIDTH / ORIGINAL_IMAGE_HEIGHT"
    />
    <section class="space-y-1" data-testid="trashedPadletDescription">
      <h3 class="text-heading-3 font-semibold text-dark-text-100 dark:text-light-text-100 text-center">
        {{ __('This padlet is in the Trash') }}
      </h3>
      <p class="text-body-small-posts text-dark-text-200 dark:text-light-text-200 break-words text-center">
        {{ description }}
      </p>
    </section>
    <OzContainedButton
      class="w-fit mx-auto"
      :color-scheme="OzContainedButtonColorScheme.Secondary"
      :text="buttonText"
      :size-preset="OzContainedButtonSizePreset.H40px"
      :should-truncate-text="false"
      :href="redirectUrl"
      data-testid="goToTrashButton"
    />
  </div>
</template>
<style lang="postcss">
@import '@@/styles/4/kit';

html {
  color-scheme: light dark;
  @apply bg-light-ui-100 dark:bg-dark-ui-100;
}

body {
  @apply flex;
  @apply items-center;
  @apply justify-center;
}
</style>
